/* kit-cart.js — cart drawer + cart page (sections kit-cart-drawer, kit-main-cart). Exposes window.KitCart. */
(function () {
  if (window.KitCart) return;

  const DRAWER_ID = 'KitCartDrawer';
  const DRAWER_SECTION = 'kit-cart-drawer';
  const cfg = window.kitConfig || {};
  const root = (cfg.routes && cfg.routes.root) || '/';

  const api = (path, body) =>
    window.Kit.fetchJSON(root + path, body ? { method: 'POST', body: JSON.stringify(body) } : undefined);

  function pageSectionId() {
    const el = document.querySelector('[data-kit-cart-page]');
    return el ? el.getAttribute('data-kit-cart-page') : null;
  }

  function readData() {
    // Prefer the drawer copy; both are rendered from the same cart.
    const el = document.querySelector('#KitCartDrawer [data-kit-cart-data]') || document.querySelector('[data-kit-cart-data]');
    try {
      return el ? JSON.parse(el.textContent) : null;
    } catch (e) {
      return null;
    }
  }

  let busy = false;
  let queued = false;
  let pendingMsg = null;

  /** Re-render drawer (+ cart page) from the server. */
  async function refresh() {
    const ids = [DRAWER_SECTION];
    const pageId = pageSectionId();
    if (pageId) ids.push(pageId);
    const res = await fetch(`${root}?sections=${ids.join(',')}`);
    const html = await res.json();
    ids.forEach((id) => {
      if (!html[id]) return;
      const doc = new DOMParser().parseFromString(html[id], 'text/html');
      const fresh = doc.querySelector('[data-kit-cart-root]');
      const target = id === DRAWER_SECTION
        ? document.querySelector('#KitCartDrawer [data-kit-cart-root]')
        : document.querySelector(`[data-kit-cart-page="${id}"] [data-kit-cart-root]`);
      if (fresh && target) target.replaceWith(fresh);
    });
    const data = readData();
    if (data) window.Kit.setCartCount(data.count);
    if (pendingMsg) {
      showCodeMsg(pendingMsg.text, pendingMsg.ok);
      pendingMsg = null;
    }
    return data;
  }

  /** Add/remove automatic free gifts to match quantity-break rules. Returns true if the cart changed. */
  async function syncGifts(data) {
    if (!data) return false;
    const updates = {};
    const adds = [];
    const ruled = new Set();
    (data.rules || []).forEach((rule) => {
      ruled.add(rule.giftVariantId);
      const count = data.lines.filter((l) => l.productId === rule.productId && !l.gift).reduce((s, l) => s + l.qty, 0);
      const giftLines = data.lines.filter((l) => l.gift && l.variantId === rule.giftVariantId);
      if (count >= rule.minQty) {
        if (!giftLines.length) adds.push({ id: rule.giftVariantId, quantity: 1, properties: { _kit_gift: '1' } });
        giftLines.forEach((l, i) => {
          if (i === 0 && l.qty !== 1) updates[l.key] = 1;
          if (i > 0) updates[l.key] = 0;
        });
      } else {
        giftLines.forEach((l) => { updates[l.key] = 0; });
      }
    });
    // gift lines whose rule disappeared (main product removed)
    data.lines.filter((l) => l.gift && !ruled.has(l.variantId)).forEach((l) => { updates[l.key] = 0; });

    let changed = false;
    if (Object.keys(updates).length) {
      await api('cart/update.js', { updates });
      changed = true;
    }
    if (adds.length) {
      try {
        await api('cart/add.js', { items: adds });
        changed = true;
      } catch (e) { /* gift out of stock: ignore */ }
    }
    return changed;
  }

  async function update(fn) {
    if (busy) {
      queued = true;
      return;
    }
    busy = true;
    document.querySelectorAll('[data-kit-cart-root]').forEach((r) => r.classList.add('is-busy'));
    try {
      if (fn) await fn();
      let data = await refresh();
      if (await syncGifts(data)) data = await refresh();
      document.dispatchEvent(new CustomEvent('kit:cart:rendered', { detail: data }));
    } catch (e) {
      window.Kit.toast((e && e.message) || (cfg.strings && cfg.strings.error) || 'Something went wrong');
    } finally {
      busy = false;
      document.querySelectorAll('[data-kit-cart-root]').forEach((r) => r.classList.remove('is-busy'));
      if (queued) {
        queued = false;
        update();
      }
    }
  }

  async function open() {
    await update();
    window.Kit.openPanel(DRAWER_ID);
  }

  /* ---------- Discount codes ---------- */
  function showCodeMsg(text, ok) {
    document.querySelectorAll('[data-kit-code-msg]').forEach((el) => {
      el.textContent = text;
      el.hidden = !text;
      el.classList.toggle('is-ok', !!ok);
    });
  }

  async function applyCode(code) {
    code = (code || '').trim();
    if (!code) return;
    const current = (readData()?.codes || []).filter((c) => c.applicable).map((c) => c.code);
    if (current.some((c) => c.toLowerCase() === code.toLowerCase())) return;
    await update(async () => {
      const cart = await api('cart/update.js', { discount: [...current, code].join(',') });
      const entry = (cart.discount_codes || []).find((c) => c.code.toLowerCase() === code.toLowerCase());
      if (entry && entry.applicable) {
        pendingMsg = { text: `${entry.code} applied`, ok: true };
      } else {
        // drop the invalid code so it doesn't linger
        await api('cart/update.js', { discount: current.join(',') });
        pendingMsg = { text: `"${code}" can't be used with this cart`, ok: false };
      }
    });
  }

  async function removeCode(code) {
    const rest = (readData()?.codes || []).filter((c) => c.applicable && c.code !== code).map((c) => c.code);
    await update(() => api('cart/update.js', { discount: rest.join(',') }));
  }

  /* ---------- Events ---------- */
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-kit-cart-trigger]');
    if (trigger && !pageSectionId()) {
      e.preventDefault();
      open();
      return;
    }
    const qtyBtn = e.target.closest('[data-kit-qty-change]');
    if (qtyBtn) {
      e.preventDefault();
      const key = qtyBtn.getAttribute('data-kit-qty-change');
      const quantity = Math.max(0, Number(qtyBtn.getAttribute('data-qty')));
      update(() => api('cart/change.js', { id: key, quantity }));
      return;
    }
    const upsell = e.target.closest('[data-kit-upsell-add]');
    if (upsell) {
      e.preventDefault();
      upsell.disabled = true;
      update(() => api('cart/add.js', { items: [{ id: Number(upsell.getAttribute('data-kit-upsell-add')), quantity: 1 }] }));
      return;
    }
    const rm = e.target.closest('[data-kit-code-remove]');
    if (rm) {
      e.preventDefault();
      removeCode(rm.getAttribute('data-kit-code-remove'));
    }
  });

  document.addEventListener('submit', (e) => {
    const form = e.target.closest('[data-kit-code-form]');
    if (!form) return;
    e.preventDefault();
    applyCode(form.querySelector('input[name="discount"]').value);
  });

  // Cart page: make sure gifts are right on load (e.g. after quantity edits elsewhere)
  document.addEventListener('DOMContentLoaded', () => {
    if (pageSectionId()) {
      const data = readData();
      syncGifts(data).then((changed) => { if (changed) update(); }).catch(() => {});
    }
  });

  window.KitCart = { open, refresh: () => update(), applyCode, removeCode };
})();
